<template>
    <div class="reports container container--full">
        <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ name: 'Organisation' }"
                >Dashboard</el-breadcrumb-item
            >
            <el-breadcrumb-item>Reports</el-breadcrumb-item>
        </el-breadcrumb>

        <h1 class="container__title h1">Reports</h1>

        <div
            class="reports__filter container__section container__section--space-small"
        >
            <div class="reports__filter__item">
                <div class="reports__filter__title h4">
                    Link types
                </div>
                <div class="reports__filter__switches">
                    <div
                        class="reports__filter__switches__item"
                        :class="{
                            'is-active': activeLinkGroupTypes.general,
                        }"
                        @click="toggleTypeFilter('general')"
                    >
                        General
                    </div>
                    <div
                        class="reports__filter__switches__item"
                        :class="{
                            'is-active': activeLinkGroupTypes.social,
                        }"
                        @click="toggleTypeFilter('social')"
                    >
                        Social
                    </div>
                    <div
                        class="reports__filter__switches__item"
                        :class="{
                            'is-active': activeLinkGroupTypes.footer,
                        }"
                        @click="toggleTypeFilter('footer')"
                    >
                        Footer
                    </div>
                </div>
            </div>

            <div class="reports__filter__item">
                <div class="reports__filter__title h4">
                    Link status
                </div>
                <div class="reports__filter__switches">
                    <div
                        class="reports__filter__switches__item"
                        :class="{
                            'is-active': activeLinkStatus.active,
                        }"
                        @click="toggleStatusFilter('active')"
                    >
                        Active
                    </div>
                    <div
                        class="reports__filter__switches__item"
                        :class="{
                            'is-active': activeLinkStatus.inactive,
                        }"
                        @click="toggleStatusFilter('inactive')"
                    >
                        Inactive
                    </div>
                    <div
                        class="reports__filter__switches__item"
                        :class="{
                            'is-active': activeLinkStatus.deleted,
                        }"
                        @click="toggleStatusFilter('deleted')"
                    >
                        Deleted
                    </div>
                </div>
            </div>

            <div class="reports__filter__item">
                <div class="reports__filter__title h4">
                    Time frame
                </div>
                <date-range-picker
                    ref="picker"
                    :opens="'right'"
                    :locale-data="{
                        firstDay: 1,
                        format: 'dd.mm.yyyy',
                    }"
                    :minDate="'1990-01-01'"
                    :maxDate="maxDate"
                    :showDropdowns="false"
                    v-model="dateRange"
                    :ranges="fixedRanges"
                    @update="dateRangeUpdated"
                >
                    <template v-slot:input="picker" style="min-width: 350px;">
                        {{ picker.startDate | date }} -
                        {{ picker.endDate | date }}
                    </template>
                </date-range-picker>
            </div>
        </div>

        <div v-if="!loading">
            <div class="container__section">
                <h3 class="container__section__title h3">Overview</h3>
                <div class="reports__detail__row">
                    <div class="reports__detail__value">
                        {{ chartOverviewData.views }} <span>total views</span>
                    </div>
                    <div class="reports__detail__value">
                        {{ chartOverviewData.clicks }} <span>total clicks</span>
                    </div>
                </div>
                <BarChart :data="chartOverviewData" />
            </div>

            <div class="reports__content">
                <div class="reports__left">
                    <div
                        v-for="activeLinkGroup in activeLinkGroups"
                        :key="activeLinkGroup.linkType"
                        class="container__section container__section--space-small"
                    >
                        <h3 class="container__section__title h3">
                            {{ activeLinkGroup.linkType }} links
                        </h3>
                        <div
                            class="reports__links"
                            v-if="activeLinkGroup.data.length > 0"
                        >
                            <div
                                v-for="link in activeLinkGroup.data"
                                :key="link.slug"
                                class="reports__links__item"
                                :class="{
                                    'is-active': link.slug === activeLink.slug,
                                }"
                                @click="showLinkDetails(link)"
                            >
                                <span class="clicks"
                                    >{{ link.clicks_count }}
                                    <span>total clicks</span></span
                                >
                                <span
                                    class="title"
                                    v-html="link.short_name"
                                ></span>
                                <a
                                    class="link"
                                    target="_blank"
                                    :href="link.url"
                                    >{{ link.url }}</a
                                >
                            </div>
                        </div>
                        <div v-else class="grey">
                            No links found with current filter settings.
                        </div>
                        <div
                            class="reports__pagination"
                            v-if="activeLinkGroup.meta"
                        >
                            <el-pagination
                                small
                                layout="prev, pager, next"
                                :total="activeLinkGroup.meta.total"
                                :page-size="pageSize"
                                :hide-on-single-page="true"
                                @current-change="
                                    switchPage(activeLinkGroup.linkType, $event)
                                "
                            >
                            </el-pagination>
                        </div>
                    </div>
                </div>
                <div class="reports__right">
                    <div class="reports__right__sticky">
                        <div class="reports__info-text" v-if="!activeLink">
                            Click a link to show its details
                        </div>
                        <div class="reports__detail" v-else>
                            <div class="reports__detail__title h3">
                                {{ activeLink.short_name }}
                            </div>
                            <div class="reports__detail__timerange">
                                {{ dateRange.startDate | date }} –
                                {{ dateRange.endDate | date }}
                            </div>
                            <div v-if="!linkDetailLoading">
                                <div class="reports__detail__row">
                                    <div class="reports__detail__value">
                                        {{ chartActiveLinkData.clicks }}
                                        <span>clicks</span>
                                    </div>
                                </div>
                                <div
                                    class="reports__detail__chart"
                                    v-if="
                                        chartActiveLinkData &&
                                            !linkDetailLoading &&
                                            !loading
                                    "
                                >
                                    <BarChart
                                        :data="chartActiveLinkData"
                                        :small="true"
                                    />
                                </div>
                                <div class="reports__detail__loader" v-else>
                                    <Loader :size="40" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="reports__loader" v-else>
            <Loader :size="48" />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Loader from '@/components/Loader.vue';
import DateRangePicker from 'vue2-daterange-picker';
import linkSortingMixin from '@/mixins/linkSortingMixin.js';
import * as api from '@/api';
import BarChart from '@/components/BarChart.vue';

export default {
    mixins: [linkSortingMixin],
    components: {
        DateRangePicker,
        BarChart,
        Loader,
    },
    filters: {
        date(date) {
            const dateObj = new Date(date);
            return `${dateObj.getDate()}.${dateObj.getMonth() +
                1}.${dateObj.getFullYear()}`;
        },
    },
    data() {
        let today = new Date();
        today.setHours(0, 0, 0, 0);

        return {
            loading: false,
            linkDetailLoading: false,
            chartOverviewData: false,
            chartActiveLinkData: false,
            pageSize: 5,
            dateRange: {
                startDate: new Date().setMonth(new Date().getMonth() - 1),
                endDate: Date.now(),
            },
            fixedRanges: {
                'This month': [
                    new Date(today.getFullYear(), today.getMonth(), 1),
                    today,
                ],
                'Last month': [
                    new Date(today.getFullYear(), today.getMonth() - 1, 1),
                    new Date(today.getFullYear(), today.getMonth(), 0),
                ],
                'Last six months': [
                    new Date(today.getFullYear(), today.getMonth() - 5, 1),
                    today,
                ],
                'This year': [
                    new Date(today.getFullYear(), 0, 1),
                    new Date(today.getFullYear(), 11, 31),
                ],
            },
            maxDate: new Date(),
            activeLink: false,
            activeLinkGroupTypes: {
                general: true,
                social: true,
                footer: true,
            },
            activeLinkStatus: {
                active: true,
                inactive: true,
                deleted: false,
            },
            links: {
                general: false,
                social: false,
                footer: false,
            },
        };
    },
    computed: {
        ...mapGetters(['activeTree']),
        activeLinkGroupTypesArray() {
            return Object.keys(this.activeLinkGroupTypes).filter(
                key => this.activeLinkGroupTypes[key] === true,
            );
        },
        activeLinkStatusArray() {
            return Object.keys(this.activeLinkStatus).filter(
                key => this.activeLinkStatus[key] === true,
            );
        },
        activeLinkGroups() {
            const linkGroups = [];
            for (const linkGroupType in this.links) {
                const linkGroup = this.links[linkGroupType];
                if (linkGroup && this.activeLinkGroupTypes[linkGroupType])
                    linkGroups.push({
                        ...linkGroup,
                        ...{ linkType: linkGroupType },
                    });
            }
            return linkGroups;
        },
    },
    mounted() {
        this.prepareLinkData();
        this.loadOverviewData();
    },
    watch: {
        activeDatasets() {
            this.loadStats(this.activeDatasets);
        },
        activeTree() {
            this.prepareLinkData();
            this.loadOverviewData();
        },
        activeLinkGroupTypes: {
            deep: true,
            handler() {
                this.activeLink = false;
                this.loadOverviewData();
            },
        },
        activeLinkStatus: {
            deep: true,
            handler() {
                this.activeLink = false;
                this.loadOverviewData();
            },
        },
    },
    methods: {
        dateRangeUpdated() {
            this.loadOverviewData();
            if (this.activeLink) this.showLinkDetails(this.activeLink);
        },
        showLinkDetails(link) {
            this.linkDetailLoading = true;
            this.activeLink = link;
            this.loadLinkDetailData(link.slug);
        },
        async loadOverviewData() {
            if (!this.activeTree.slug) return;
            this.loading = true;
            try {
                // load data for overview
                const filterData = {
                    from: this.getDashedDateString(this.dateRange.startDate),
                    to: this.getDashedDateString(this.dateRange.endDate),
                    link_types: this.activeLinkGroupTypesArray.join(','),
                    statuses: this.activeLinkStatusArray.join(','),
                };
                const overviewResponse = await api.linktree.getOverviewStats(
                    this.activeTree.slug,
                    filterData,
                );
                this.chartOverviewData = overviewResponse.data;

                // load the link lists
                for (const linkType of this.activeLinkGroupTypesArray) {
                    const additionalFilterData = {
                        link_types: linkType,
                        per_page: this.pageSize,
                        page: 1,
                    };
                    const response = await api.linktree.getLinkStats(
                        this.activeTree.slug,
                        { ...filterData, ...additionalFilterData },
                    );
                    this.links[linkType] = response.data;
                }
                this.loading = false;
            } catch (error) {
                console.error(error);
            }
        },
        async switchPage(linkType, pageIndex) {
            try {
                const filterData = {
                    from: this.getDashedDateString(this.dateRange.startDate),
                    to: this.getDashedDateString(this.dateRange.endDate),
                    link_types: linkType,
                    per_page: this.pageSize,
                    page: pageIndex,
                    statuses: this.activeLinkStatusArray.join(','),
                };
                const response = await api.linktree.getLinkStats(
                    this.activeTree.slug,
                    filterData,
                );
                this.links[linkType] = response.data;
            } catch (error) {
                console.error(error);
            }
        },
        async loadLinkDetailData(linkSlug) {
            try {
                const filterData = {
                    from: this.getDashedDateString(this.dateRange.startDate),
                    to: this.getDashedDateString(this.dateRange.endDate),
                };
                const response = await api.link.getStats(linkSlug, filterData);
                this.chartActiveLinkData = response.data;
                this.linkDetailLoading = false;
            } catch (error) {
                console.error(error);
            }
        },
        toggleTypeFilter(type) {
            if (
                this.activeLinkGroupTypesArray.length === 1 &&
                this.activeLinkGroupTypesArray[0] === type
            )
                return;
            this.activeLinkGroupTypes[type] = !this.activeLinkGroupTypes[type];
        },
        toggleStatusFilter(status) {
            if (
                this.activeLinkStatusArray.length === 1 &&
                this.activeLinkStatusArray[0] === status
            )
                return;
            this.activeLinkStatus[status] = !this.activeLinkStatus[status];
        },
        getDashedDateString(date) {
            const dateObj = new Date(date);
            return `${dateObj.getFullYear()}-${dateObj.getMonth() +
                1}-${dateObj.getDate()}`;
        },
    },
};
</script>

<style lang="scss">
.reports {
    &__content {
        @include mq(desktop) {
            display: flex;
        }
    }

    &__left {
        @include mq(desktop) {
            width: 30%;
        }

        @include mq(notebook) {
            width: 40%;
        }
    }

    &__right {
        padding-top: 80px;

        @include mq(desktop) {
            width: 70%;
            padding-top: 0;
            padding-left: 40px;
        }

        @include mq(notebook) {
            width: 60%;
            padding-left: 60px;
        }

        &__sticky {
            @include mq(desktop) {
                position: sticky;
                top: 40px;
                left: 0;
            }
        }
    }

    &__info-text {
        text-align: center;
        padding: 40px 0;
    }

    &__filter {
        display: flex;
        flex-wrap: wrap;

        &__item {
            margin-bottom: 40px;

            &:not(:last-child) {
                margin-right: 80px;
            }
        }

        &__title {
            margin-bottom: 10px;
        }

        &__switches {
            display: flex;
            overflow: hidden;

            &__item {
                border: solid 1px $medium-light-grey;
                text-transform: uppercase;
                font-size: 12px;
                padding: 10px;
                color: $medium-light-grey;
                margin-right: 5px;
                cursor: pointer;

                &.is-active {
                    color: $black;
                    border-color: $black;
                }
            }
        }
    }

    &__detail {
        &__timerange {
            color: $medium-grey;
            font-size: 14px;
        }

        &__row {
            display: flex;
            margin-top: 20px;
            margin-bottom: 40px;
        }

        &__value {
            &:not(:last-child) {
                margin-right: 50px;
            }

            span {
                color: $medium-grey;
                font-size: 14px;
            }
        }

        &__chart {
            max-width: 100%;
        }
    }

    &__links {
        margin-bottom: 10px;

        &__item {
            display: block;
            background: $barely-white;
            box-sizing: border-box;
            padding: 10px 15px;
            margin-bottom: 5px;
            overflow: hidden;
            transition: $link-transition;
            cursor: pointer;

            &.is-active {
                background: $medium-light-grey;
                color: white;

                a,
                span.clicks span {
                    color: $light-grey;
                }
            }

            span {
                &.clicks {
                    float: right;

                    span {
                        font-size: 10px;
                        color: $medium-grey;
                    }
                }
                &.title {
                    display: block;
                    max-width: 75%;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
            a {
                max-width: 75%;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: inline-block;
                font-size: 11px;
                color: $medium-grey;
            }
        }
    }

    &__pagination {
        display: flex;
        justify-content: flex-end;
    }

    &__loader {
        margin-top: 20px;
        display: flex;
        justify-content: center;
    }
}
</style>
